import { alpha, Box, useTheme } from "@mui/material";
import type { DateTime } from "luxon";
import { type FC, useState } from "react";
import { ALPHA, NEUTRAL, OPEN_SHIFTS, SHADOW, SURFACE } from "../../../../theme/tokens";
import type { OpenShiftRequest, ReplaceShiftRequest, RequestOrigins } from "../../../../utils/models";
import { countNurseResponses } from "../../../../utils/utilFunctions";
import { GapsRequestAnswers } from "./GapsRequestAnswers";
import { GapsRequestCardHeader } from "./GapsRequestCardHeader";
import { type GapsApplicant, useGapsApplicants } from "./useGapsApplicants";
import { useGapsRequestLabels } from "./useGapsRequestLabels";

type Props = {
	request: OpenShiftRequest | ReplaceShiftRequest;
	/** Answered, and starting inside the urgent window (`isUrgentClaim`) — the 48h band's cards. */
	isUrgentClaim: boolean;
	/** The shift starts inside the urgent window, answered or not. */
	startsSoon: boolean;
	/**
	 * Shows its answers without being asked: an urgent claim, or a list narrowed to answered requests (the artifact's
	 * rule). Followed live — switching to the Answered filter opens the cards already on screen, and switching back folds
	 * the ones the filter opened — while a card the user opened stays open.
	 */
	isExpandedByList: boolean;
	/** A robo call is already out for this request (the call-off agent's own list). */
	isRoboCallRunning: boolean;
	/** Why the request exists; `null` where the department does not get origins. */
	origins: RequestOrigins | null;
	/** "Now", as the list last read it (it moves on each minute), so every card measures "starts in" from the same instant. */
	now: DateTime;
	onOpenRequest: (request: OpenShiftRequest | ReplaceShiftRequest) => void;
	onApprove: (request: OpenShiftRequest | ReplaceShiftRequest, applicant: GapsApplicant) => void;
};

/**
 * One posted request on Open Shifts (P3-12): the shift's hours as the headline, the job as small print, and — once it
 * is answered — one line per response with a labelled Approve, three at first and the rest behind "Show N more".
 *
 * The headline is the request itself: tapping it opens the posted-shift sheet (escalate, agency, incentive, hours,
 * how many, add someone, delete). The answers below it are the decision, so an urgent claim — answered and starting
 * within 48 hours — opens with them showing and carries the band's red edge; any other answered request folds them
 * behind one line until asked, which is also when their overtime is fetched.
 */
export const GapsRequestCard: FC<Props> = ({
	request,
	isUrgentClaim,
	startsSoon,
	isExpandedByList,
	isRoboCallRunning,
	origins,
	now,
	onOpenRequest,
	onApprove,
}) => {
	const theme = useTheme();
	const { hoursLabel } = useGapsRequestLabels();
	// Derived, not seeded: cards are keyed by request, so a flag read once at mount never saw the list switch to Answered.
	// The card has no fold control, so the user's own "see who" only ever opens it. The overtime this fetches is one
	// batch per opened card — the classic list predicts once per applicant on every card it draws.
	const [isOpenedByUser, setIsOpenedByUser] = useState<boolean>(false);
	const isExpanded = isOpenedByUser || isExpandedByList;
	const responseCount = countNurseResponses(request);
	const { applicants } = useGapsApplicants(request, isExpanded && responseCount > 0);

	const testId = `gaps-request-${request.id}`;
	// `toRelative` names no time only for an invalid start, which `startsSoon` already excludes.
	const startsIn = startsSoon ? request.message.shift_start.toRelative({ base: now }) : null;
	const urgentInk = theme.palette.error.main;

	return (
		<Box
			data-testid={testId}
			data-urgent={isUrgentClaim}
			sx={{
				position: "relative",
				bgcolor: SURFACE,
				borderRadius: OPEN_SHIFTS.CARD_RADIUS,
				border: `1px solid ${isUrgentClaim ? alpha(urgentInk, ALPHA.STATUS_BORDER) : NEUTRAL[200]}`,
				boxShadow: SHADOW.PANEL_SOFT,
				overflow: "hidden",
				// The band's red edge: the one thing on the screen that says "decide this first".
				...(isUrgentClaim && {
					"&::before": { content: '""', position: "absolute", left: 0, top: 0, bottom: 0, width: OPEN_SHIFTS.URGENT_EDGE_PX, bgcolor: urgentInk },
				}),
			}}
		>
			<GapsRequestCardHeader
				request={request}
				isUrgentClaim={isUrgentClaim}
				startsIn={startsIn}
				responseCount={responseCount}
				isRoboCallRunning={isRoboCallRunning}
				origins={origins}
				onOpenRequest={onOpenRequest}
				testId={testId}
			/>
			<GapsRequestAnswers
				request={request}
				responseCount={responseCount}
				isExpanded={isExpanded}
				applicants={applicants}
				onReview={() => setIsOpenedByUser(true)}
				formatHours={hoursLabel}
				onApprove={onApprove}
				testId={testId}
			/>
		</Box>
	);
};
